/* De Niro size stack — derives the hidden Fit variant from the customer's Height choice
   (CC/C/MC → Standard, MA/A → Tall · X-Tall) or the Made-to-Measure toggle, gates
   add-to-cart until the visible panel's spec is complete, and narrates the selected
   size's fit ranges from the chart.

   The mode toggle renders ABOVE the Foot Size dropdown and the panels BELOW it — both
   outside <product-variant-listener> (whose contents are re-fetched and replaced on
   every variant change), paired up via data-form-id. All handlers are document-
   delegated: the Fit option radios DO live inside the swap zone, so element references
   to them must never be cached. */
(() => {
  if (window.__rbcDeniroSize) return;
  window.__rbcDeniroSize = true;

  const roots = () => document.querySelectorAll('[data-deniro-size]');
  const toggleFor = (root) =>
    document.querySelector('[data-dns-toggle][data-form-id="' + root.dataset.formId + '"]');
  const rootForToggle = (toggle) =>
    document.querySelector('[data-deniro-size][data-form-id="' + toggle.dataset.formId + '"]');

  /* ShowTime drives the submitted variant from the option radios carrying
     data-variant-id (see fit-finder.js selectFootVariant) — check the radio,
     dispatch change, keep the sibling a11y <select> label in sync. */
  function setFit(root, fitValue, force) {
    const idx = root.dataset.fitIndex;
    const scope = root.parentElement.querySelector('product-variant-selector') ||
      document.querySelector('product-variant-selector');
    if (!scope) return false;
    const radios = scope.querySelectorAll('input[type="radio"][data-option-index="' + idx + '"]');
    for (const r of radios) {
      if (String(r.value).trim() === fitValue) {
        /* force: the radio can already be ticked while the variant never followed (its change event fired before
           the variant machinery was listening) — say it again. */
        if (!r.checked || force) {
          r.checked = true;
          r.dispatchEvent(new Event('change', { bubbles: true }));
          const dd = r.closest('product-options-dropdown');
          const sel = dd && dd.querySelector('select');
          if (sel) sel.value = r.value;
          return true;
        }
        return false;
      }
    }
    return false;
  }

  function fitForHeight(root) {
    const h = root.querySelector('[data-dns-height]');
    const opt = h && h.selectedOptions && h.selectedOptions[0];
    return (opt && opt.dataset.fit) || 'Standard';
  }

  function deriveFitFromHeight(root) {
    return setFit(root, fitForHeight(root));
  }

  /* The Fit of a variant id, from the map the server rendered on the root ("id:Fit|id:Fit…"). */
  function fitOfVariant(root, id) {
    if (!root._fitMap) {
      root._fitMap = {};
      (root.dataset.fitMap || '').split('|').forEach((pair) => {
        const at = pair.indexOf(':');
        if (at > 0) root._fitMap[pair.slice(0, at)] = pair.slice(at + 1).trim();
      });
    }
    return root._fitMap[String(id)] || '';
  }

  const holdNote = (form, text) => {
    let note = form.querySelector('[data-rbc-hold-note]');
    if (!note) {
      note = document.createElement('p');
      note.setAttribute('data-rbc-hold-note', '');
      note.setAttribute('role', 'alert');
      note.style.marginTop = '.75rem';
      form.appendChild(note);
    }
    note.textContent = text;
  };

  /* The variant about to be submitted is not the one the chosen height calls for: the Fit's change event was lost
     (it fired before the variant machinery was listening) or the variant change is still in flight. Nothing is
     added. The Fit is announced again at once — and once more when the variant machinery's own elements are all
     defined, if they were not yet — and the press goes through by itself as soon as the form carries the right
     variant. If it never does, the customer is told; the wrong variant is never submitted. One at a time per form
     (by id: the theme can replace the form node). */
  const VARIANT_PARTS = ['product-variant-selector', 'product-variant-provider', 'product-variant-listener'];
  const settling = new Map(); // form id → { want, cancel } for the press being held
  function settleVariant(root, formId, want) {
    if (settling.has(formId)) return;
    const started = Date.now();
    const say = () => setFit(root, want, true);
    const pending = window.customElements
      ? VARIANT_PARTS.filter((n) => document.querySelector(n) && !customElements.get(n))
      : [];
    say();
    if (pending.length) {
      Promise.all(pending.map((n) => customElements.whenDefined(n)))
        .then(() => requestAnimationFrame(() => { if (settling.has(formId)) say(); }));
    }
    const release = (form) => form.querySelectorAll('[data-to-cart]').forEach((b) => b.classList.remove('!loading'));
    let timer = 0;
    const end = () => { clearInterval(timer); settling.delete(formId); };
    /* cancel: the customer changed their mind while this was pending (another height, or over to Made to Measure).
       The press belonged to the old choice: drop it — nothing added, no message — and hand the button back. Called
       from the poll, and at once by the gate when a new submit arrives after such a change. */
    const cancel = (keepLoading) => { end(); const f = document.getElementById(formId); if (f && !keepLoading) release(f); };
    settling.set(formId, { want, cancel });
    timer = setInterval(() => {
      const live = document.getElementById(formId);
      if (!live) { end(); return; }
      if (mtmModeOn(root) || fitForHeight(root) !== want) { cancel(); return; }
      if (fitOfVariant(root, new FormData(live).get('id')) === want) {
        end(); // from here the gate lets a submit through again — and the very next line is that submit
        const button = live.querySelector('[data-to-cart]');
        try {
          if (!live.requestSubmit) release(live);
          else if (button && button.form === live && !button.disabled) { button.classList.add('!loading'); live.requestSubmit(button); }
          else live.requestSubmit();
        } catch (err) { release(live); }
      } else if (Date.now() - started > 15000) {
        end();
        release(live);
        holdNote(live, 'We could not update the price for that boot height, so nothing was added. Please choose the height again, then add to cart.');
      } else {
        // the form's contents are re-rendered while the variant changes: keep the new button in its loading state
        const button = live.querySelector('[data-to-cart]');
        if (button && !button.classList.contains('!loading')) button.classList.add('!loading');
      }
    }, 100);
  }

  function mtmModeOn(root) {
    const toggle = toggleFor(root);
    return !!(toggle && toggle.querySelector('input[data-dns-mode][value="mtm"]:checked'));
  }

  /* The Fit the server rendered for this page load: the radio carrying the checked ATTRIBUTE. (The property can
     already have been moved — by a script, or by the browser restoring form state on reload.) */
  function serverFit(root) {
    const scope = root.parentElement.querySelector('product-variant-selector') ||
      document.querySelector('product-variant-selector');
    const r = scope && scope.querySelector(
      'input[type="radio"][data-option-index="' + root.dataset.fitIndex + '"][checked]');
    return r ? String(r.value).trim() : '';
  }

  /* Start-up ADOPTS the variant the page loaded on; it never overwrites it. A page can load on any Fit variant
     (a shared link; Google Shopping lists every variant separately). The toggle is rendered "standard" and sits
     outside the variant swap zone, so start-up used to derive "Standard" from the untouched height select and tick
     that radio — before the variant machinery was listening, so the change event was lost. The selector then showed
     Standard while the page still priced and submitted the loaded variant: a Made to Measure boot (+£160) went
     through the standard panel with no measurement walkthrough. */
  function adoptLoadedVariant(root) {
    const toggle = toggleFor(root);
    if (!toggle) return;
    const mode = serverFit(root) === 'Made to Measure' ? 'mtm' : 'standard';
    const radio = toggle.querySelector('input[data-dns-mode][value="' + mode + '"]');
    if (radio && !radio.checked) radio.checked = true;
  }

  /* init: show the panel for the adopted mode and stop there. Writing the Fit radio belongs to the customer's own
     actions (the toggle, the height select), by which time the variant machinery is listening. */
  function syncMode(root, init) {
    const mtmOn = mtmModeOn(root);
    root.querySelectorAll('[data-dns-panel]').forEach((panel) => {
      const active = panel.dataset.dnsPanel === (mtmOn ? 'mtm' : 'standard');
      panel.hidden = !active;
      panel.querySelectorAll('select, input').forEach((c) => { c.disabled = !active; });
    });
    if (!init) {
      if (mtmOn) setFit(root, 'Made to Measure');
      else deriveFitFromHeight(root);
    }
    syncEta(root);
  }

  /* ---- Personal fit line under the Height/Calf selects: how the SELECTED size sits
     against the customer's OWN measurements (the Fit Finder's saved state for this
     product). Chart ranges stay internal (Simon 2026-07-03) — no measurements, no
     line. Mirrors the finder's maths exactly (mod-adjusted values, same range
     parsing, same good-fit tolerances) so the two surfaces can never disagree. ---- */
  let ffCfg = null;
  function loadCfg() {
    if (ffCfg !== null) return ffCfg;
    ffCfg = false;
    try {
      const cfgEl = document.getElementById('rbc-fitfinder-config');
      const cfg = cfgEl && JSON.parse(cfgEl.textContent);
      if (cfg && cfg.chart && cfg.chart.found) ffCfg = cfg;
    } catch (err) { /* no fit line */ }
    return ffCfg;
  }
  function parseRange(rangeStr) {
    // "45.6-47.5cm (Tall)" → { lo: 45.6, hi: 47.5 }
    const m = String(rangeStr).match(/([\d.]+)\s*-\s*([\d.]+)/);
    return m ? { lo: parseFloat(m[1]), hi: parseFloat(m[2]) } : null;
  }
  function modFor(modStr, shoe) {
    // Same per-shoe adjustment table the Fit Finder applies (calfmod:/heightmod: tags).
    if (!modStr || isNaN(shoe)) return 0;
    for (const part of String(modStr).split(';')) {
      const kv = part.split('|');
      if (kv.length === 2 && parseFloat(kv[0]) === shoe) {
        const m = parseFloat(kv[1]);
        return isNaN(m) ? 0 : m;
      }
    }
    return 0;
  }
  function variantScope(root) {
    return root.parentElement.querySelector('product-variant-selector') ||
      document.querySelector('product-variant-selector');
  }
  function measurements(root) {
    // The customer's saved calf/leg from the finder: raw for display, mod-adjusted
    // (the finder's own frame) for the range verdict, plus the selected foot.
    const cfg = loadCfg();
    if (!cfg) return null;
    let saved = null;
    try {
      saved = JSON.parse(localStorage.getItem('rbcFitFinder:' + root.dataset.productId)
        || localStorage.getItem('rbcFitFinder:profile') || '{}').values || null;
    } catch (err) { return null; }
    if (!saved) return null;
    const calfRaw = parseFloat(saved.calfSize);
    const legRaw = parseFloat(saved.legHeight);
    if (isNaN(calfRaw) && isNaN(legRaw)) return null;
    let shoe = NaN;
    const scope = variantScope(root);
    const checked = scope ? scope.querySelectorAll('input[type="radio"][data-option-index]:checked') : [];
    for (const r of checked) {
      const v = String(r.value).trim();
      if ((cfg.footSizes || []).indexOf(v) !== -1) {
        const m = v.match(/\d+(\.\d+)?/);
        shoe = m ? parseFloat(m[0]) : NaN;
        break;
      }
    }
    if (!isNaN(shoe)) shoe = cfg.chart.hasHalfSizes ? Math.round(shoe * 2) / 2 : Math.round(shoe);
    return {
      shoe,
      calfRaw,
      legRaw,
      calf: isNaN(calfRaw) ? NaN : calfRaw + modFor(cfg.mods && cfg.mods.calf, shoe),
      leg: isNaN(legRaw) ? NaN : legRaw + modFor(cfg.mods && cfg.mods.height, shoe),
    };
  }
  /* The finder's good-fit boundary (fit-finder.js calfTol/legTol, the aboveGood/
     belowGood columns), mirrored so a size the wizard calls a good fit is never
     flagged here. Severity grading beyond the boundary stays the wizard's job. */
  function fitsWell(kind, range, val) {
    const cfg = loadCfg();
    const fit = (cfg && cfg.fit) || {};
    if (val >= range.lo && val <= range.hi) return true;
    if (kind === 'calf') {
      return val > range.hi
        ? val - range.hi <= (fit.stretch ? 1.0 : 0)
        : range.lo - val <= 0.5;
    }
    return false; // leg height: the finder flags anything outside the band
  }
  /* Qualifier quoting the two numbers ON SHOW: the customer's raw measurement vs
     the size's exact per-foot value. */
  function nomDelta(kind, raw, nominal) {
    const d = Math.round(Math.abs(raw - nominal) * 10) / 10;
    if (!d) return '';
    if (kind === 'calf') return d + 'cm ' + (raw > nominal ? 'snug' : 'roomy');
    return d + 'cm ' + (raw > nominal ? 'short' : 'tall');
  }
  function deltaText(kind, range, val) {
    // Words only — a numeric delta here could only come from the internal band
    // maths, and bands stay customer-invisible (Simon 2026-07-06).
    if (val >= range.lo && val <= range.hi) return 'a match';
    if (kind === 'calf') {
      // Measurement above the range = boot snugger than the leg; below = roomier.
      return val > range.hi ? 'on the snug side' : 'on the roomy side';
    }
    // Leg height: measurement above the range = boot shorter than the leg; below = taller.
    return val > range.hi ? 'on the short side' : 'on the tall side';
  }
  function updateChartLine(root) {
    const line = root.querySelector('[data-dns-chartline]');
    if (!line) return;
    const cfg = loadCfg();
    const m = cfg && measurements(root);
    if (!m) { line.hidden = true; line.replaceChildren(); return; }
    const h = root.querySelector('[data-dns-height]');
    const calfSel = root.querySelector('[data-dns-calf]');
    const vals = cfg.chart.values;
    const rows = [];
    if (h && h.selectedIndex > 0 && cfg.chart.legRanges[h.selectedIndex - 1] && !isNaN(m.leg)) {
      // Height options are declared in chart order (CC → A), so index maps directly.
      const r = parseRange(cfg.chart.legRanges[h.selectedIndex - 1]);
      const codeMatch = (h.selectedOptions[0].textContent || '').match(/\(([A-Z]+)\)/);
      const nom = vals && codeMatch && vals.heights && vals.heights[codeMatch[1]] &&
        vals.heights[codeMatch[1]][String(m.shoe)];
      if (r) rows.push(rowText('Height', 'height', m.legRaw, nom, r, m.leg));
    }
    if (calfSel && calfSel.value && !isNaN(m.calf)) {
      const match = cfg.chart.calfRanges.find((s) => String(s).indexOf('(' + calfSel.value + ')') !== -1);
      const r = match && parseRange(match);
      const nom = vals && vals.calves && vals.calves[calfSel.value] &&
        vals.calves[calfSel.value][String(m.shoe)];
      if (r) rows.push(rowText('Calf', 'calf', m.calfRaw, nom, r, m.calf));
    }
    line.replaceChildren();
    const usable = rows.filter(Boolean);
    if (usable.length) {
      // Yours-vs-this-size mini grid (Simon 2026-07-03: eyeballable, and pretty).
      const grid = document.createElement('span');
      grid.className = 'rbc-dns__fitgrid';
      ['', 'Yours', 'This size'].forEach((t) => {
        const hd = document.createElement('span');
        hd.className = 'rbc-dns__fith';
        hd.textContent = t;
        grid.appendChild(hd);
      });
      usable.forEach((e) => {
        const dim = document.createElement('span');
        dim.className = 'rbc-dns__fitdim';
        dim.textContent = e.label;
        grid.appendChild(dim);
        const yours = document.createElement('span');
        yours.className = 'rbc-dns__fitval';
        yours.textContent = isNaN(e.raw) ? '—' : e.raw + ' cm';
        grid.appendChild(yours);
        const size = document.createElement('span');
        size.className = 'rbc-dns__fitval';
        size.textContent = (e.nominal || e.nominal === 0) ? e.nominal + ' cm' : '—';
        if (e.note) {
          const n = document.createElement('span');
          n.className = 'rbc-dns__fitnote';
          n.textContent = e.note;
          size.appendChild(n);
        }
        grid.appendChild(size);
      });
      line.appendChild(grid);
    }
    line.hidden = !usable.length;
  }
  /* One grid entry per dimension: raw measurement vs the size's exact per-foot
     value (ranges internal); honest qualifier only when the fit sits outside
     the finder's good-fit boundary. */
  function rowText(label, kind, raw, nominal, range, adjusted) {
    const good = fitsWell(kind, range, adjusted);
    const inRange = adjusted >= range.lo && adjusted <= range.hi;
    const hasNom = nominal || nominal === 0;
    return {
      label,
      raw,
      nominal: hasNom ? nominal : null,
      // With both numbers on show, a good fit needs no words, and any qualifier
      // quotes the difference of THOSE numbers — never the internal band maths,
      // which sits 0.4–0.5cm off the nominal and reads as wrong arithmetic
      // ("34 vs 31 · 2.6cm snug" — Simon 2026-07-06). Without a nominal (brands
      // lacking per-foot values) the verdict text carries the meaning alone.
      note: good ? (hasNom ? '' : (inRange ? 'a match' : 'a good fit'))
        : hasNom ? nomDelta(kind, raw, nominal)
        : deltaText(kind, range, adjusted),
    };
  }

  /* ---- Delivery expectation above add-to-cart. MTO products are always 4–5 weeks
     (static Liquid text). Stocked products flip to made-in when a Tall/X-Tall or
     MTM build is selected — those are never on a shelf. ---- */
  function syncEta(root) {
    const eta = document.querySelector('[data-dns-eta][data-form-id="' + root.dataset.formId + '"]');
    if (!eta || eta.dataset.etaBase !== 'stocked') return;
    let standardBuild = !mtmModeOn(root);
    if (standardBuild) {
      const h = root.querySelector('[data-dns-height]');
      const opt = h && h.selectedOptions && h.selectedOptions[0];
      if (opt && opt.dataset.fit && opt.dataset.fit !== 'Standard') standardBuild = false;
    }
    eta.textContent = standardBuild ? eta.dataset.etaStocked : eta.dataset.etaMto;
  }

  document.addEventListener('change', (e) => {
    if (e.target.matches && e.target.matches('input[data-dns-mode]')) {
      const toggle = e.target.closest('[data-dns-toggle]');
      const root = toggle && rootForToggle(toggle);
      if (root) syncMode(root);
      return;
    }
    /* Foot-size radios live inside the variant swap zone, not inside the stack —
       a foot change moves the mod-adjusted measurements (and half-size rounding),
       so the personal fit line must recompute. Fit radios match too; harmless. */
    if (e.target.matches && e.target.matches('input[type="radio"][data-option-index]')) {
      roots().forEach((root) => {
        const scope = variantScope(root);
        if (scope && scope.contains(e.target)) { updateChartLine(root); syncEta(root); }
      });
      return;
    }
    const root = e.target.closest && e.target.closest('[data-deniro-size]');
    if (!root) return;
    /* Height→Fit derivation belongs to STANDARD mode only. In MTM mode the Fit
       variant is owned by the toggle (syncMode) — the height select is hidden
       there, so a change can only be programmatic (the Fit Finder prefilling the
       standard panel after an MTM confirm), and deriving from it would silently
       flip the just-selected Made to Measure variant back to Standard. */
    if (e.target.matches('[data-dns-height]')) { if (!mtmModeOn(root)) deriveFitFromHeight(root); updateChartLine(root); syncEta(root); }
    else if (e.target.matches('[data-dns-calf]')) updateChartLine(root);
    if (e.target.classList.contains('rbc-mtm__invalid') && e.target.checkValidity()) {
      e.target.classList.remove('rbc-mtm__invalid');
    }
  });

  /* Wizard integration: the Fit Finder owns MTM capture. Show the captured-summary
     state when its walkthrough completes (or was completed in a previous visit),
     and hide the find-my-size trigger when no wizard config is on the page. */
  function wizardConfig() { return document.getElementById('rbc-fitfinder-config'); }
  /* The walkthrough on the page belongs to ONE product. A size stack for a different product (a quick view, a
     featured-product block) has no walkthrough here, whatever config happens to be on the page. */
  function wizardConfigFor(root) {
    const el = wizardConfig();
    if (!el) return null;
    try {
      const cfg = JSON.parse(el.textContent);
      return String(cfg.productId) === String(root.dataset.productId) ? el : null;
    } catch (err) { return null; }
  }
  function refreshMtmSummary(root) {
    const summary = root.querySelector('[data-dns-mtm-summary]');
    if (!summary) return;
    let complete = false;
    try {
      const s = JSON.parse(localStorage.getItem('rbcFitFinder:' + root.dataset.productId)
        || localStorage.getItem('rbcFitFinder:profile') || '{}');
      complete = !!s.mtmComplete;
    } catch (err) { /* stay hidden */ }
    summary.hidden = !complete;
  }
  document.addEventListener('rbc:mtm-complete', (e) => {
    const productId = e.detail && e.detail.productId;
    roots().forEach((root) => {
      if (productId && String(root.dataset.productId) !== String(productId)) return;
      refreshMtmSummary(root);
    });
  });
  roots().forEach((root) => {
    refreshMtmSummary(root);
    updateChartLine(root);
    syncEta(root);
    if (!wizardConfigFor(root)) {
      root.querySelectorAll('.rbc-dns__finder').forEach((b) => { b.hidden = true; });
    }
  });

  /* The Fit Finder's off-chart verdict routes the customer to made-to-measure
     (rbc:choose-mtm). Flip the toggle so the +£160 MTM variant is selected and
     the walkthrough panel opens. */
  document.addEventListener('rbc:choose-mtm', (e) => {
    const productId = e.detail && e.detail.productId;
    roots().forEach((root) => {
      /* Scope to the finder's product — a quick-view can put a second stack on
         the page, and it must not flip too. No id in the event → act only when
         the stack is unambiguous. */
      if (productId ? String(root.dataset.productId) !== String(productId) : roots().length !== 1) return;
      const toggle = toggleFor(root);
      const mtmRadio = toggle && toggle.querySelector('input[data-dns-mode][value="mtm"]');
      if (mtmRadio && !mtmRadio.checked) {
        mtmRadio.checked = true;
        mtmRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  });

  /* Gate add-to-cart: the boot is unbuildable without its spec. Capture phase +
     stopImmediatePropagation so the theme's fetch-cart handler never sees an
     incomplete submit. */
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!form.matches || !form.matches('form[action*="/cart/add"]')) return;
    const formId = form.getAttribute('id');
    const root = Array.from(roots()).find((r) => r.dataset.formId === formId);
    if (!root) return;
    /* A variant settlement (settleVariant) is holding a press for this form and will submit it itself: any other
       submit meanwhile — the customer pressing again — would add the boot twice, so it is swallowed (the button is
       in its loading state throughout). Unless the customer has since changed the height or switched to Made to
       Measure: then the held press is cancelled here and now, and this submit is judged afresh below. */
    const held = settling.get(formId);
    if (held) {
      if (mtmModeOn(root) || fitForHeight(root) !== held.want) held.cancel(true); // this submit owns the button's loading state now; a check below that stops it hands the button back
      else { e.preventDefault(); e.stopImmediatePropagation(); return; }
    }
    const unstick = () => form.querySelectorAll('[data-to-cart]').forEach((b) => b.classList.remove('!loading'));
    /* Made to Measure needs the walkthrough's gate RUNNING for this product — its configuration on the page is not
       enough (the script can fail to load), and a config for another product is no use at all. Refuse rather than
       let an unbuildable order through; the shared hold also takes a customer on another product's page to this
       boot's own page. */
    if (mtmModeOn(root) && (!wizardConfigFor(root) || String(window.rbcFitFinderGate || '') !== String(root.dataset.productId))) {
      if (window.rbcHoldForMeasurements) window.rbcHoldForMeasurements(e, form);
      else { e.preventDefault(); e.stopImmediatePropagation(); unstick(); }
      return;
    }
    const invalid = [];
    root.querySelectorAll('select[required], input[required]').forEach((c) => {
      if (c.disabled) return;
      /* checkValidity() covers required AND the number constraints (min/max/step) —
         the form is novalidate, but the element-level constraint API still works. */
      const bad = !c.checkValidity();
      c.classList.toggle('rbc-mtm__invalid', bad);
      if (bad) invalid.push(c);
    });
    if (invalid.length) {
      e.preventDefault();
      e.stopImmediatePropagation();
      /* product-to-cart set !loading on the button at click time and only clears it
         on cart:update/cart:error — which never arrive for a blocked submit. */
      unstick();
      invalid[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
      invalid[0].focus({ preventScroll: true });
      return;
    }
    /* Standard panel: the variant being submitted must be the one the chosen height calls for (Tall heights are a
       separately priced variant). Checked here, at the moment of submission, against the id in the form — not
       inferred from the Fit radio, and whatever the reason they differ. */
    if (!mtmModeOn(root)) {
      const want = fitForHeight(root);
      const have = fitOfVariant(root, new FormData(form).get('id'));
      if (have && have !== want) {
        e.preventDefault();
        e.stopImmediatePropagation();
        settleVariant(root, formId, want);
      }
    }
  }, true);

  roots().forEach((root) => { adoptLoadedVariant(root); syncMode(root, true); });

  /* A height chosen BEFORE this script ran (a fast hand on a slow connection, or the browser restoring the form on
     reload) never reached the change handler. Best effort, for the price on show: once the variant selector is
     defined, derive the Fit from it. An untouched select is left alone — start-up must not overwrite the loaded
     variant. Correctness does not rest on this: the add-to-cart check above compares the submitted variant with the
     chosen height every time. */
  if (document.querySelector('product-variant-selector') && window.customElements) {
    customElements.whenDefined('product-variant-selector').then(() => requestAnimationFrame(() => {
      roots().forEach((root) => {
        const h = root.querySelector('[data-dns-height]');
        if (h && h.value && !mtmModeOn(root)) deriveFitFromHeight(root);
      });
    }));
  }

  /* The add-to-cart checks above are in place: tell the inline guard (rbc-measure-guard.js), which until now has been
     keeping any press of Add to cart on this form, to replay it. */
  window.rbcSizeReady = Object.assign(window.rbcSizeReady || {}, { deniro: true });
  document.dispatchEvent(new CustomEvent('rbc:size:ready'));
})();
